import React from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import { PamLoading, PamError } from '../components/PamStateComponents'
import { useAuth } from './AuthContext'

interface ProtectedRouteProps {
  requireAdmin?: boolean
  /**
   * Task 9 (fix-wave 2026-07-24): a server-permission string (exact wire
   * value from pam_permissions.go, e.g. "audit:query") the caller's
   * server-resolved `permissions` (GET /v1/admin/whoami) must include for
   * this route's content to render. Route-level, defense-in-depth ONLY —
   * the server remains the actual enforcement point on every API call this
   * page makes; see ui/e2e/rbac-harness.spec.ts for the server-side proof.
   * Omit for routes that require no permission beyond `requireAdmin`
   * (unchanged legacy behavior).
   */
  requiredPermission?: string
  children: React.ReactNode
}

// ---------------------------------------------------------------------------
// Themed sign-in prompt (styled-components + @devnw/theme tokens — replaces
// the previous MUI Box/Card/CardContent/Typography/Button tree). See
// .superpowers/sdd/oidcpam-gate6-shared.md — OIDCPAM Gate-6 Phase 7.
// ---------------------------------------------------------------------------

const Screen = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`

const PromptCard = styled.div`
  max-width: 400px;
  width: 90%;
  padding: ${({ theme }) => ut(asUnified(theme)).space('lg')};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 8}px;
  background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#ffffff'};
  box-shadow: ${({ theme }) => ut(asUnified(theme)).boxShadow('sm') ?? '0 1px 3px rgba(0,0,0,0.2)'};
`

const PromptTitle = styled.h2`
  margin: 0 0 ${({ theme }) => ut(asUnified(theme)).space('sm')};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.lg ?? '1.25rem'};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

const PromptBody = styled.p`
  margin: 0 0 ${({ theme }) => ut(asUnified(theme)).space('md')};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
`

const SignInButton = styled.button`
  min-height: 36px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('md')}`};
  border: none;
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 2px;
  }
`

const NotAuthorizedCard = styled.div`
  max-width: 400px;
  width: 90%;
  padding: ${({ theme }) => ut(asUnified(theme)).space('lg')};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 8}px;
  background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#ffffff'};
  box-shadow: ${({ theme }) => ut(asUnified(theme)).boxShadow('sm') ?? '0 1px 3px rgba(0,0,0,0.2)'};
`

export const ProtectedRoute = ({
  requireAdmin = false,
  requiredPermission,
  children,
}: ProtectedRouteProps) => {
  const { token, claims, login, permissions, permissionsStatus, refreshPermissions } =
    useAuth()

  const isAuthenticated = token !== null
  const isAdmin = (claims?.roles ?? []).includes('admin')

  if (!isAuthenticated || (requireAdmin && !isAdmin)) {
    return (
      <Screen>
        <PromptCard>
          <PromptTitle>Sign in required</PromptTitle>
          <PromptBody>
            You must be signed in{requireAdmin ? ' as an administrator' : ''} to access this page.
          </PromptBody>
          <SignInButton type="button" onClick={() => login()}>
            Sign in
          </SignInButton>
        </PromptCard>
      </Screen>
    )
  }

  // Task 9: route-level, defense-in-depth permission gate. The server
  // remains the actual enforcement point on every request this page makes —
  // this only prevents the PAGE ITSELF from rendering for a caller who
  // cannot use it (e.g. typing /admin/audit as a policy-admin).
  if (requiredPermission) {
    // "Do not lock users out": while the whoami call is still in flight (or
    // hasn't started — 'idle' means the token JUST became available and the
    // fetch effect hasn't run yet), show a loading state, never a denial —
    // an authorized caller must never see a false "not authorized" screen
    // during the async resolution window.
    if (permissionsStatus === 'idle' || permissionsStatus === 'loading') {
      return (
        <Screen>
          <PamLoading label="Checking your permissions…" />
        </Screen>
      )
    }

    // The whoami fetch failed. This is deliberately NOT the same as "no
    // permissions" (which would fail-open-adjacent by silently rendering
    // "not authorized" for a caller we simply failed to check) and NOT the
    // same as "allow" (which would recreate the original fail-open bug) —
    // it is its own distinct, retryable state.
    if (permissionsStatus === 'error') {
      return (
        <Screen>
          <PamError
            message="Couldn't verify your permissions. Your access may still be valid — try again."
            onRetry={() => {
              refreshPermissions().catch(() => {
                // Intentional no-op: refreshPermissions already routes its
                // own failure into permissionsStatus === 'error' — this
                // catch exists only so a rejected promise from the retry
                // click handler itself can never surface as an unhandled
                // rejection.
              })
            }}
          />
        </Screen>
      )
    }

    // permissionsStatus === 'resolved': a definitive answer is available.
    if (!(permissions ?? []).includes(requiredPermission)) {
      return (
        <Screen>
          <NotAuthorizedCard role="alert">
            <PromptTitle>Access denied</PromptTitle>
            <PromptBody>
              Your admin role does not include the permission required to view
              this page. Contact an administrator if you believe this is
              incorrect.
            </PromptBody>
          </NotAuthorizedCard>
        </Screen>
      )
    }
  }

  return <>{children}</>
}
